<template>
  <div
    class="ysc-map relative w-full"
    :class="aspect"
  >
    <div class="absolute top-0 h-full w-full">
      <ClientOnly>
        <LMap
          ref="map"
          :min-zoom="3"
          :zoom="zoom"
          :max-zoom="isUndetailed ? 13 : 18"
          :center="value || [0, 0]"
          :options="mapOption"
          :use-global-leaflet="false"
          @ready="onMapReady"
        >
          <LControlLayers
            v-if="tileProviders.length > 1"
            position="bottomleft"
          />

          <LTileLayer
            v-for="tileProvider in tileProviders"
            :key="tileProvider.name"
            :name="tileProvider.name"
            :visible="tileProvider.visible"
            :options="tileProvider.options"
            :url="tileProvider.url"
            :attribution="tileProvider.options.attribution"
            :tile-size="tileProvider.options.tileSize"
            layer-type="base"
          />

          <template v-if="definedCoords">
            <LCircle
              v-if="isUndetailed"
              ref="circle"
              :lat-lng="definedCoords"
              :radius="750"
              :options="{
                className: 'text-pink-500 fill-current stroke-current stroke-1',
                interactive: false,
              }"
            />

            <LMarker
              v-else-if="!products.length"
              :lat-lng="definedCoords"
              :options="{
                interactive: false,
              }"
            >
              <LIcon
                :icon-size="[32, 32]"
                :icon-anchor="[16, 32]"
              >
                <YscIconsMarkerSolidTarget
                  v-if="isDraggable"
                  class="h-full w-full text-black"
                />
                <YscIconsMarkerSolid
                  v-else
                  class="h-full w-full text-pink-500"
                />
              </LIcon>
            </LMarker>
          </template>

          <template v-else>
            <LMarker
              v-for="product in products"
              :key="`product-${product.id}`"
              :lat-lng="[product.vehicle_location_latitude, product.vehicle_location_longitude]"
              @click="onMarkerClick(product)"
            >
              <LIcon
                :icon-size="[53, 33]"
                :icon-anchor="[0, 16]"
              >
                <span
                  class="search-marker relative whitespace-nowrap rounded border border-gray-300 bg-white font-semibold text-black transition-all hover:bg-black hover:text-white"
                  :class="{
                    'marked-item': markedItemId === product.id || hoveredItemId === product.id,
                    'px-2.5 py-1.5 text-base': zoom >= 8,
                    'px-1.5 py-1 text-sm': zoom < 8,
                  }"
                >
                  {{ getSearchCamperDisplayPrice(product) }}
                </span>
              </LIcon>
            </LMarker>
          </template>
        </LMap>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LatLngBoundsExpression, Map } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { LMap, LIcon, LMarker, LControlLayers, LTileLayer, LCircle } from '@vue-leaflet/vue-leaflet'
import type { AppCamperSearch } from '@yescapa-dev/ysc-api-js/modern'

interface Props {
  value?: [number, number]
  aspect?: string
  products?: AppCamperSearch[]
  isUndetailed?: boolean
  isDraggable?: boolean
  showSatellite?: boolean
  skipFitBounds?: boolean
  skipWatchFitBounds?: boolean
  // To reduce costs with stadia, we generate an interactive map from which we remove the controls.
  isFakeStatic?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  value: undefined,
  aspect: 'aspect-square',
  products: () => [],
  isUndetailed: false,
  isDraggable: false,
  showSatellite: true,
  skipFitBounds: false,
  skipWatchFitBounds: false,
  isFakeStatic: false,
})

const emit = defineEmits<{
  'input': [{ cords: { lat: number | null, lng: number | null }, radius: number, center: { lat: number, lng: number } }]
  'marker-click': [AppCamperSearch]
}>()

const { t } = useI18n()

const { markedItemId, hoveredItemId } = storeToRefs(useSearchStore())

const coords = ref<{
  lat: number | null
  lng: number | null
}>({
  lat: null,
  lng: null,
})

const { getSearchCamperDisplayPrice } = useGetSearchCamperDisplayPrice()

const zoom = ref(props.isFakeStatic ? 13 : 7)

const mapOption = props.isFakeStatic
  ? {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: false,
    }
  : undefined

const map = ref()

const leafletObject = computed(() => {
  if (!map.value) {
    return null
  }

  return map.value.leafletObject as Map
})

const { pixelRatio: devicePixelRatio } = useDevicePixelRatio()

const mapResolution = computed(() => {
  return devicePixelRatio.value > 1 ? '@2x' : ''
})

const definedCoords = computed(() => {
  const { lat, lng } = toValue(coords)
  if (!isDefined(lat) || !isDefined(lng)) {
    return undefined
  }
  return { lat, lng }
})

const tileProviders = computed(() => {
  const options = {
    attribution: '© <a href="https://stadiamaps.com/">Stadia Maps</a>',
    tileSize: 256,
  }

  const providers = [
    {
      name: t('components.ysc_map.street'),
      visible: true,
      url: `https://tiles-eu.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}${
        mapResolution.value
      }.png`,
      options,
    },
  ]

  if (props.showSatellite) {
    providers.push({
      name: t('components.ysc_map.street'),
      visible: false,
      url: `https://tiles-eu.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}${
        mapResolution.value
      }.png`,
      options,
    })
  }
  return providers
})

const fitBounds = (bounds: LatLngBoundsExpression) => {
  if (isDefined(leafletObject.value)) {
    leafletObject.value.fitBounds(bounds)
  }
}

const fitBoundsToProducts = () => {
  if (isDefined(leafletObject.value)) {
    const productCoords = props.products
      .filter(p => isDefined(p.vehicle_location_latitude) && isDefined(p.vehicle_location_longitude))
      .map(p => [p.vehicle_location_latitude, p.vehicle_location_longitude])

    fitBounds(productCoords)
  }
}

const updateMarker = () => {
  if (props.value && isDefined(leafletObject.value)) {
    const latlng = leafletObject.value.getCenter()
    coords.value = { lat: latlng.lat, lng: latlng.lng }
  }
}

const updateMap = () => {
  if (!isDefined(leafletObject.value)) {
    return
  }

  updateMarker()

  if (!props.skipFitBounds) {
    if (isDefined(props.value)) {
      fitBounds([props.value])
      return
    }

    if (props.products.length) {
      fitBoundsToProducts()
      return
    }

    leafletObject.value.fitWorld()
  }
}

const onMapDrag = () => {
  updateMarker()
}

const emitData = () => {
  const mapBoundNorthEast = map.value.leafletObject.getBounds().getNorthEast()
  const mapDistance = mapBoundNorthEast.distanceTo(map.value.leafletObject.getCenter())
  emit('input', { cords: coords.value, radius: mapDistance, center: map.value.leafletObject.getCenter() })
}

const onMarkerClick = (camper: AppCamperSearch) => {
  emit('marker-click', camper)
}

const onMapClick = () => {
  if (props.products.length) {
    markedItemId.value = null
  }
}

const onZoomEnd = () => {
  updateMarker()
  emitData()
}

const listenerAdded = ref(false)
const onMapReady = () => {
  if (!isDefined(leafletObject.value)) {
    return
  }

  if (props.isDraggable && !listenerAdded.value) {
    leafletObject.value.on('drag', onMapDrag)
    leafletObject.value.on('dragend', emitData)
    leafletObject.value.on('zoomend', onZoomEnd)
    leafletObject.value.on('click', onMapClick)
    listenerAdded.value = true
  }

  updateMap()
}

onBeforeUnmount(() => {
  if (listenerAdded.value && isDefined(leafletObject.value)) {
    leafletObject.value.off('drag', onMapDrag)
    leafletObject.value.off('dragend', emitData)
    leafletObject.value.off('zoomend', onZoomEnd)
    leafletObject.value.off('click', onMapClick)
  }
})

watch(() => [props.value, props.products], () => {
  updateMap()
})
</script>

<style>
.ysc-map {
  @apply z-0;
  .leaflet-control-layers-base {
    @apply space-y-2;

    label {
      @apply cursor-pointer;

      & > div {
        @apply font-lexend flex items-center space-x-2 text-base antialiased;
      }
    }

    .leaflet-control-layers-selector {
      @apply top-0 mt-0;
    }
  }
  .leaflet-div-icon {
    @apply bg-transparent border-none;
  }
  .search-marker:after,
  .search-marker:before {
    @apply absolute right-full top-1/2 h-0 w-0 border border-gray-300 transition;
    border: solid transparent;
    content: '';
  }
  .search-marker:after {
    border-color: rgba(255, 255, 255, 0);
    border-right-color: white;
    border-width: 6px;
    margin-top: -6px;
  }
  .search-marker:before {
    @apply border border-gray-300/0;
    border-right-color: theme('colors.gray.300');
    border-width: 7px;
    margin-top: -7px;
  }
  .search-marker:hover:after {
    border-color: rgba(136, 183, 213, 0);
    border-right-color: theme('colors.black');
  }
  .marked-item {
    @apply bg-black !text-white hover:bg-black hover:text-white;
  }
  .marked-item::after {
    border-right: solid 6px theme('colors.black');
  }
}
</style>
